<script context="module" lang="ts">
    import { get, writable } from "svelte/store"

    export type ReadingTabPanelMode = "collapsed" | "side" | "expanded"

    export const readingTabPanelMode = writable<ReadingTabPanelMode>("collapsed")

    export function collapseReadingTabPanel() {
        readingTabPanelMode.set("collapsed")
    }

    export function showReadingTabPanelSide() {
        readingTabPanelMode.set("side")
    }

    export function expandReadingTabPanel() {
        readingTabPanelMode.set("expanded")
    }

    export function toggleReadingTabPanelExpanded() {
        readingTabPanelMode.set(get(readingTabPanelMode) === "expanded" ? "collapsed" : "expanded")
    }
</script>

<script lang="ts">
    import EditTab from "@components/popup/templates/EditTab.svelte"
    import Icon from "@components/material/Icon.svelte"
    import { audioPlayerHeight } from "@components/audiobook/Player.svelte"
    import { open } from "@components/navigator/Navigator.svelte"
    import { selectTab, selectedTab, selectedTabID, tabs } from "@store/pageTracker"
    import { deviceSize } from "@utils/material/responsive"
    import MobileReadingTabStrip from "./MobileReadingTabStrip.svelte"
    import ReadingTabExpandedView from "./ReadingTabExpandedView.svelte"
    import ReadingTabPanelItem from "./ReadingTabPanelItem.svelte"

    let addTabDialogueOpen = false
    let tabCountBeforeCreate = 0
    let tabNameTooltip: { title: string; top: number; left: number } | null = null

    $: tabList = [...$tabs.values()]
    $: mobile = $deviceSize !== "desktop"
    $: effectiveMode =
        mobile && $readingTabPanelMode === "side" ? "collapsed" : $readingTabPanelMode
    $: expanded = effectiveMode === "expanded"
    $: compact = !expanded && (mobile || effectiveMode === "collapsed")
    $: toggleLabel = compact ? "Show reading tab list" : "Collapse reading tabs"
    $: if (!compact || mobile) tabNameTooltip = null

    function collapseAfterSelection() {
        if (mobile || effectiveMode !== "side") collapseReadingTabPanel()
    }

    function browseWithoutTab() {
        selectTab("abandoned")
        collapseAfterSelection()
        open()
    }

    function togglePanel() {
        if (mobile) {
            expandReadingTabPanel()
            return
        }

        if (effectiveMode === "side") {
            collapseReadingTabPanel()
            return
        }

        showReadingTabPanelSide()
    }

    function openCreateTabDialog() {
        tabCountBeforeCreate = tabList.length
        addTabDialogueOpen = true
    }

    function closeCreateTabDialog() {
        addTabDialogueOpen = false
        if ($tabs.size > tabCountBeforeCreate) collapseReadingTabPanel()
    }

    function showTabNameTooltip(event: CustomEvent<{ title: string; top: number; left: number }>) {
        if (!compact || mobile) return
        tabNameTooltip = event.detail
    }

    function hideTabNameTooltip() {
        tabNameTooltip = null
    }
</script>

{#if addTabDialogueOpen}
    <EditTab tab={null} on:exit={closeCreateTabDialog} />
{/if}

<aside
    class="reading-tab-panel"
    class:collapsed={compact}
    class:side={!compact && !expanded}
    class:mobileDock={mobile && compact}
    class:expanded
    style={mobile && compact ? `--reader-tabs-mobile-bottom-offset: ${$audioPlayerHeight}px;` : ""}
    aria-label="Reading tabs"
>
    {#if expanded}
        <ReadingTabExpandedView
            {tabList}
            selectedTab={$selectedTab}
            selectedTabID={$selectedTabID}
            createTab={openCreateTabDialog}
            {browseWithoutTab}
            collapsePanel={collapseReadingTabPanel}
        />
    {:else if mobile && compact}
        <MobileReadingTabStrip
            {tabList}
            selectedTabID={$selectedTabID}
            createTab={openCreateTabDialog}
            {browseWithoutTab}
            on:select={collapseReadingTabPanel}
        />
    {:else}
        <div class="panel-header">
            <div class="panel-title">
                <span>Reading</span>
                <h2>Tabs</h2>
            </div>
            <button
                type="button"
                class="collapse-button"
                aria-label={toggleLabel}
                aria-pressed={compact}
                data-tooltip={compact ? "Show tabs" : "Collapse tabs"}
                on:click={togglePanel}
            >
                <Icon icon={mobile ? "arrow-right" : compact ? "arrow-right" : "arrow"} />
            </button>
        </div>

        <div class="browse-tab-zone" role="list" aria-label="Browse without a reading tab">
            <div role="listitem">
                <button
                    type="button"
                    class="rail-action browse-action"
                    class:active={$selectedTabID === "abandoned"}
                    aria-label="Browse without using a reading tab"
                    aria-pressed={$selectedTabID === "abandoned"}
                    data-tooltip="Browse"
                    on:click={browseWithoutTab}
                >
                    <span class="action-edge action-edge-top" aria-hidden="true"></span>
                    <span class="action-edge action-edge-bottom" aria-hidden="true"></span>
                    <span class="action-icon" aria-hidden="true"
                        ><Icon icon="table-of-contents" /></span
                    >
                    <span class="action-label">Browse</span>
                </button>
            </div>
        </div>

        <div class="tabs-scroll">
            {#if tabList.length}
                <div class="tabs-list" role="list" aria-label="Open reading tabs">
                    {#each tabList as tab (tab.id)}
                        <div role="listitem">
                            <ReadingTabPanelItem
                                {tab}
                                collapsed={compact}
                                selected={tab.id === $selectedTabID}
                                on:select={collapseAfterSelection}
                                on:tooltipshow={showTabNameTooltip}
                                on:tooltiphide={hideTabNameTooltip}
                            />
                        </div>
                    {/each}
                </div>
            {:else}
                <div class="empty-state">
                    <span class="empty-icon" aria-hidden="true">
                        <Icon icon="bookmarks" />
                    </span>
                    <span class="empty-title">No tabs yet</span>
                    <span class="empty-copy">Create one to keep your place as you read.</span>
                </div>
            {/if}
        </div>

        <div class="panel-actions">
            <button
                type="button"
                class="rail-action"
                aria-label="Create new reading tab"
                data-tooltip="New tab"
                on:click={openCreateTabDialog}
            >
                <span class="action-icon" aria-hidden="true"><Icon icon="plus" /></span>
                <span class="action-label">New tab</span>
            </button>
        </div>

        {#if tabNameTooltip && compact && !mobile}
            <div
                class="tab-name-tooltip"
                style={`top: ${tabNameTooltip.top}px; left: ${tabNameTooltip.left}px;`}
                aria-hidden="true"
            >
                {tabNameTooltip.title}
            </div>
        {/if}
    {/if}
</aside>

<style>
    .reading-tab-panel {
        --reader-tabs-surface: var(--navBackground);
        --reader-tabs-border: var(--color-purple-100);
        --reader-tabs-text: var(--color-grey-700);
        --reader-tabs-muted: var(--color-grey-500);
        --reader-tabs-strong: var(--color-grey-900);
        --reader-tabs-hover-bg: var(--color-purple-50);
        --reader-tabs-tab-bg: var(--color-grey-0);
        --reader-tabs-tab-border: var(--color-purple-100);
        --reader-tabs-tab-hover-border: var(--color-purple-200);
        --reader-tabs-icon-bg: var(--color-purple-50);
        --reader-tabs-icon-text: var(--color-purple-700);
        --reader-tabs-active-bg: var(--background);
        --reader-tabs-active-border: var(--color-purple-100);
        --reader-tabs-active-text: var(--color-grey-900);
        --reader-tabs-active-muted: var(--color-grey-600);
        --reader-tabs-active-icon-bg: var(--color-purple-50);
        --reader-tabs-active-icon-text: var(--color-purple-800);
        --reader-tabs-utility-bg: transparent;
        --reader-tabs-utility-border: transparent;
        --reader-tabs-utility-text: var(--reader-tabs-muted);
        --reader-tabs-utility-hover-bg: var(--reader-tabs-hover-bg);
        --reader-tabs-utility-hover-border: var(--reader-tabs-tab-border);
        --reader-tabs-utility-hover-text: var(--reader-tabs-strong);
        --reader-tabs-utility-active-bg: var(--color-purple-50);
        --reader-tabs-utility-active-border: var(--color-purple-100);
        --reader-tabs-utility-active-text: var(--color-purple-700);
        --reader-tabs-utility-icon-bg: transparent;
        --reader-tabs-utility-active-icon-bg: var(--color-purple-100);
        --reader-tabs-tooltip-bg: var(--color-grey-900);
        --reader-tabs-tooltip-text: var(--color-grey-0);
        --reader-tabs-edge-fill: 0.58rem;
        --reader-tabs-utility-inset-end: 0.45rem;
        --reader-tabs-panel-border-width: 1px;
        --reader-tabs-desktop-divider-border: var(--reader-tabs-active-border);
        --reader-tabs-mobile-backdrop: var(--reader-tabs-surface);
        --reader-tabs-mobile-tab-bg: var(--reader-tabs-tab-bg);
        --reader-tabs-mobile-tab-border: var(--reader-tabs-tab-border);
        --reader-tabs-mobile-tab-top-border: var(--reader-tabs-tab-border);
        --reader-tabs-mobile-tab-hover-bg: var(--reader-tabs-hover-bg);
        --reader-tabs-mobile-tab-hover-border: var(--reader-tabs-tab-hover-border);
        --reader-tabs-mobile-tab-text: var(--reader-tabs-text);
        --reader-tabs-mobile-tab-hover-text: var(--reader-tabs-strong);
        --reader-tabs-mobile-tab-icon-bg: var(--reader-tabs-icon-bg);
        --reader-tabs-mobile-arrow-text: var(--reader-tabs-icon-text);
        --reader-tabs-mobile-active-bg: var(--reader-tabs-active-bg);
        --reader-tabs-mobile-active-border: var(--reader-tabs-active-border);
        --reader-tabs-mobile-active-text: var(--reader-tabs-active-text);
        --reader-tabs-mobile-active-muted: var(--reader-tabs-active-muted);
        --reader-tabs-mobile-active-icon-bg: var(--reader-tabs-active-icon-bg);
        --reader-tabs-mobile-active-icon-text: var(--reader-tabs-active-icon-text);
        --reader-tabs-mobile-utility-bg: var(--reader-tabs-utility-bg);
        --reader-tabs-mobile-utility-border: var(--reader-tabs-utility-border);
        --reader-tabs-mobile-utility-text: var(--reader-tabs-utility-text);
        --reader-tabs-mobile-utility-hover-bg: var(--reader-tabs-utility-hover-bg);
        --reader-tabs-mobile-utility-hover-border: var(--reader-tabs-utility-hover-border);
        --reader-tabs-mobile-utility-hover-text: var(--reader-tabs-utility-hover-text);
        --reader-tabs-mobile-utility-active-bg: var(--reader-tabs-utility-active-bg);
        --reader-tabs-mobile-utility-active-border: var(--reader-tabs-utility-active-border);
        --reader-tabs-mobile-utility-active-text: var(--reader-tabs-utility-active-text);

        grid-area: reader-tabs;
        display: grid;
        grid-template-rows: auto auto minmax(0, 1fr) auto;
        gap: 0.38rem;
        min-width: 0;
        min-height: 0;
        padding: calc(var(--safe-area-inset-top) + 0.7rem) 0
            calc(var(--safe-area-inset-bottom) + 0.7rem) 0.55rem;
        border-right: 0;
        background: var(--reader-tabs-surface);
        color: var(--reader-tabs-text);
        position: relative;
        z-index: 4;
        overflow: visible;
    }

    .reading-tab-panel::before {
        content: "";
        position: absolute;
        inset: 0 0 0 auto;
        border-right: var(--reader-tabs-panel-border-width) solid
            var(--reader-tabs-desktop-divider-border);
        pointer-events: none;
        z-index: 0;
    }

    .reading-tab-panel.expanded {
        --reader-tabs-surface: var(--background);

        position: fixed;
        inset: 0 0 0 var(--app-primary-nav-width, 5.25rem);
        z-index: 7;
        display: block;
        min-width: 0;
        min-height: 0;
        padding: calc(var(--safe-area-inset-top) + 1rem)
            max(1rem, var(--safe-area-inset-left, 0rem)) calc(var(--safe-area-inset-bottom) + 1rem)
            max(1rem, var(--safe-area-inset-right, 0rem));
        border: 0;
        background: var(--reader-tabs-surface);
        overflow-y: auto;
        overflow-x: hidden;
        animation: reading-tabs-expanded-in 0.2s ease-out;
    }

    .reading-tab-panel.expanded::before {
        content: none;
    }

    :global(.darkmode) .reading-tab-panel {
        --reader-tabs-surface: var(--color-grey-900);
        --reader-tabs-border: var(--color-grey-800);
        --reader-tabs-text: var(--color-grey-300);
        --reader-tabs-muted: var(--color-grey-400);
        --reader-tabs-strong: var(--color-grey-50);
        --reader-tabs-hover-bg: var(--color-grey-800);
        --reader-tabs-tab-bg: var(--color-grey-800);
        --reader-tabs-tab-border: var(--color-grey-700);
        --reader-tabs-tab-hover-border: var(--color-grey-600);
        --reader-tabs-icon-bg: var(--color-grey-800);
        --reader-tabs-icon-text: var(--color-grey-200);
        --reader-tabs-active-bg: var(--background);
        --reader-tabs-active-border: var(--color-grey-700);
        --reader-tabs-active-text: var(--color-grey-0);
        --reader-tabs-active-muted: var(--color-grey-300);
        --reader-tabs-active-icon-bg: var(--color-grey-800);
        --reader-tabs-active-icon-text: var(--color-grey-100);
        --reader-tabs-utility-bg: transparent;
        --reader-tabs-utility-border: transparent;
        --reader-tabs-utility-text: var(--reader-tabs-muted);
        --reader-tabs-utility-hover-bg: var(--reader-tabs-hover-bg);
        --reader-tabs-utility-hover-border: var(--reader-tabs-tab-border);
        --reader-tabs-utility-hover-text: var(--reader-tabs-strong);
        --reader-tabs-utility-active-bg: var(--color-grey-800);
        --reader-tabs-utility-active-border: var(--color-grey-700);
        --reader-tabs-utility-active-text: var(--color-grey-100);
        --reader-tabs-utility-icon-bg: transparent;
        --reader-tabs-utility-active-icon-bg: var(--color-grey-700);
        --reader-tabs-tooltip-bg: var(--color-grey-50);
        --reader-tabs-tooltip-text: var(--color-grey-900);
    }

    :global(.darkmode) .reading-tab-panel.expanded {
        --reader-tabs-surface: var(--background);
    }

    .panel-header {
        position: relative;
        z-index: 1;
        display: grid;
        grid-template-columns: minmax(0, 1fr) 2rem;
        align-items: center;
        gap: 0.4rem;
        min-width: 0;
        padding-right: var(--reader-tabs-utility-inset-end);
    }

    .panel-title {
        display: grid;
        gap: 0.18rem;
        min-width: 0;
        padding-inline: 0.3rem;
    }

    .panel-title span,
    .panel-title h2 {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .panel-title span {
        color: var(--reader-tabs-muted);
        font-size: 0.68rem;
        font-weight: 800;
        letter-spacing: 0.025em;
        line-height: 1;
        text-transform: uppercase;
    }

    .panel-title h2 {
        margin: 0;
        color: var(--reader-tabs-strong);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.1rem;
        font-weight: 500;
        line-height: 1.15;
        letter-spacing: 0;
    }

    .collapse-button,
    .rail-action {
        appearance: none;
        position: relative;
        display: grid;
        align-items: center;
        border: 0;
        border-radius: 0.45rem;
        box-shadow: none;
        background: transparent;
        color: var(--reader-tabs-text);
        cursor: pointer;
        font: inherit;
        letter-spacing: 0;
        text-transform: none;
        transition:
            background-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .collapse-button {
        place-items: center;
        width: 2rem;
        height: 2rem;
        justify-self: end;
    }

    .collapse-button:hover,
    .rail-action:hover {
        background: var(--reader-tabs-hover-bg);
        color: var(--reader-tabs-strong);
    }

    .collapse-button:active,
    .rail-action:active {
        transform: translateY(1px);
    }

    .collapse-button:focus-visible,
    .rail-action:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .collapse-button :global(svg),
    .action-icon :global(svg),
    .empty-icon :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .browse-tab-zone,
    .tabs-scroll {
        position: relative;
        z-index: 1;
        min-width: 0;
    }

    .browse-tab-zone {
        padding: 0.14rem 0 0.04rem 0.05rem;
    }

    .tabs-scroll {
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding: var(--reader-tabs-edge-fill) 0 var(--reader-tabs-edge-fill) 0.05rem;
    }

    .tabs-list {
        display: grid;
        gap: 0.14rem;
        min-width: 0;
    }

    .empty-state {
        display: grid;
        justify-items: center;
        gap: 0.45rem;
        min-width: 0;
        padding: 0.8rem 0.25rem;
        text-align: center;
    }

    .empty-icon {
        display: grid;
        place-items: center;
        width: 2rem;
        height: 2rem;
        border-radius: 0.45rem;
        background: var(--reader-tabs-icon-bg);
        color: var(--reader-tabs-icon-text);
    }

    .empty-title {
        color: var(--reader-tabs-strong);
        font-size: 0.86rem;
        font-weight: 780;
        line-height: 1.2;
    }

    .empty-copy {
        color: var(--reader-tabs-muted);
        font-size: 0.74rem;
        line-height: 1.35;
    }

    .panel-actions {
        position: relative;
        z-index: 1;
        display: grid;
        gap: 0.28rem;
        min-width: 0;
        padding-right: var(--reader-tabs-utility-inset-end);
    }

    .rail-action {
        grid-template-columns: 1.7rem minmax(0, 1fr);
        gap: 0.5rem;
        width: 100%;
        min-height: 2.35rem;
        border: 1px solid transparent;
        padding: 0.28rem 0.4rem;
        text-align: left;
    }

    .browse-action {
        width: calc(100% - var(--reader-tabs-utility-inset-end));
        min-height: 2.15rem;
        margin-right: var(--reader-tabs-utility-inset-end);
        border-color: var(--reader-tabs-utility-border);
        border-radius: 0.48rem;
        background: var(--reader-tabs-utility-bg);
        color: var(--reader-tabs-utility-text);
        opacity: 0.94;
    }

    .browse-action:hover {
        border-color: var(--reader-tabs-utility-hover-border);
        background: var(--reader-tabs-utility-hover-bg);
        color: var(--reader-tabs-utility-hover-text);
        opacity: 1;
    }

    .browse-action.active {
        border-color: var(--reader-tabs-utility-active-border);
        border-radius: 0.48rem;
        background: var(--reader-tabs-utility-active-bg);
        color: var(--reader-tabs-utility-active-text);
        opacity: 1;
    }

    .rail-action.active:not(.browse-action) {
        border-color: var(--reader-tabs-active-border);
        border-right-color: var(--reader-tabs-active-bg);
        border-radius: 0.62rem 0 0 0.62rem;
        background: var(--reader-tabs-active-bg);
        color: var(--reader-tabs-active-text);
        z-index: 2;
    }

    .action-edge {
        display: none;
        position: absolute;
        right: calc(-1 * var(--reader-tabs-panel-border-width));
        width: var(--reader-tabs-edge-fill);
        height: var(--reader-tabs-edge-fill);
        pointer-events: none;
    }

    .rail-action.active:not(.browse-action) .action-edge {
        display: block;
    }

    .action-edge-top {
        top: calc(-1 * var(--reader-tabs-edge-fill));
        border-right: 1px solid var(--reader-tabs-active-border);
        border-bottom: 1px solid var(--reader-tabs-active-border);
        border-bottom-right-radius: var(--reader-tabs-edge-fill);
        box-shadow: 0.24rem 0.24rem 0 var(--reader-tabs-active-bg);
    }

    .action-edge-bottom {
        bottom: calc(-1 * var(--reader-tabs-edge-fill));
        border-top: 1px solid var(--reader-tabs-active-border);
        border-right: 1px solid var(--reader-tabs-active-border);
        border-top-right-radius: var(--reader-tabs-edge-fill);
        box-shadow: 0.24rem -0.24rem 0 var(--reader-tabs-active-bg);
    }

    .action-icon {
        display: grid;
        place-items: center;
        width: 1.7rem;
        height: 1.7rem;
        border-radius: 0.45rem;
        background: var(--reader-tabs-icon-bg);
        color: var(--reader-tabs-icon-text);
    }

    .browse-action .action-icon {
        background: var(--reader-tabs-utility-icon-bg);
        color: currentColor;
    }

    .browse-action .action-label {
        color: currentColor;
    }

    .rail-action.active:not(.browse-action) .action-icon {
        background: var(--reader-tabs-active-icon-bg);
        color: var(--reader-tabs-active-icon-text);
    }

    .browse-action.active .action-icon {
        background: var(--reader-tabs-utility-active-icon-bg);
    }

    .rail-action.active:not(.browse-action) .action-label {
        color: var(--reader-tabs-active-text);
    }

    .action-label {
        min-width: 0;
        overflow: hidden;
        color: var(--reader-tabs-strong);
        font-size: 0.82rem;
        font-weight: 760;
        line-height: 1.1;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .reading-tab-panel.collapsed {
        padding-left: 0.55rem;
        padding-right: 0;
    }

    .reading-tab-panel.collapsed .panel-header,
    .reading-tab-panel.collapsed .browse-action,
    .reading-tab-panel.collapsed .rail-action {
        grid-template-columns: 1fr;
        justify-items: center;
    }

    .reading-tab-panel.collapsed .panel-title,
    .reading-tab-panel.collapsed .action-label,
    .reading-tab-panel.collapsed .empty-title,
    .reading-tab-panel.collapsed .empty-copy {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }

    .reading-tab-panel.collapsed .empty-state {
        padding-inline: 0;
    }

    .reading-tab-panel.collapsed .rail-action {
        min-height: 2.35rem;
        padding: 0.32rem;
    }

    .reading-tab-panel.collapsed .collapse-button {
        justify-self: center;
    }

    .reading-tab-panel.collapsed .collapse-button::after,
    .reading-tab-panel.collapsed .rail-action::after {
        content: attr(data-tooltip);
        position: absolute;
        top: 50%;
        left: calc(100% + 0.75rem);
        min-width: max-content;
        max-width: 12rem;
        padding: 0.42rem 0.7rem;
        border-radius: 999px;
        background: var(--reader-tabs-tooltip-bg);
        color: var(--reader-tabs-tooltip-text);
        font-size: 0.78rem;
        font-weight: 800;
        line-height: 1;
        opacity: 0;
        pointer-events: none;
        transform: translate(0.35rem, -50%);
        transition:
            opacity 0.14s ease,
            transform 0.14s ease;
        white-space: nowrap;
        z-index: 20;
    }

    .reading-tab-panel.collapsed .collapse-button:hover::after,
    .reading-tab-panel.collapsed .collapse-button:focus-visible::after,
    .reading-tab-panel.collapsed .rail-action:hover::after,
    .reading-tab-panel.collapsed .rail-action:focus-visible::after {
        opacity: 1;
        transform: translate(0, -50%);
    }

    .tab-name-tooltip {
        position: fixed;
        max-width: 14rem;
        overflow: hidden;
        padding: 0.42rem 0.7rem;
        border-radius: 999px;
        background: var(--reader-tabs-tooltip-bg);
        color: var(--reader-tabs-tooltip-text);
        font-size: 0.78rem;
        font-weight: 800;
        line-height: 1;
        pointer-events: none;
        text-overflow: ellipsis;
        transform: translateY(-50%);
        white-space: nowrap;
        z-index: 30;
    }

    @media only screen and (max-width: 1153px) {
        .reading-tab-panel {
            --reader-tabs-surface: var(--navBackground);
            --reader-tabs-border: var(--color-purple-100);
            --reader-tabs-text: var(--color-grey-700);
            --reader-tabs-muted: var(--color-grey-500);
            --reader-tabs-strong: var(--color-grey-900);
            --reader-tabs-hover-bg: var(--color-purple-50);
            --reader-tabs-tab-bg: var(--color-grey-0);
            --reader-tabs-tab-border: var(--color-purple-100);
            --reader-tabs-tab-hover-border: var(--color-purple-200);
            --reader-tabs-icon-bg: var(--color-purple-50);
            --reader-tabs-icon-text: var(--color-purple-700);
            --reader-tabs-active-bg: var(--background);
            --reader-tabs-active-border: var(--color-purple-100);
            --reader-tabs-active-text: var(--color-grey-900);
            --reader-tabs-active-muted: var(--color-grey-600);
            --reader-tabs-active-icon-bg: var(--color-purple-50);
            --reader-tabs-active-icon-text: var(--color-purple-800);
            --reader-tabs-utility-active-bg: var(--color-purple-50);
            --reader-tabs-utility-active-border: var(--color-purple-100);
            --reader-tabs-utility-active-text: var(--color-purple-700);

            grid-template-rows: auto minmax(0, 1fr) auto;
            gap: 0.45rem;
            padding: 0.55rem 0.65rem;
            border-top: 1px solid var(--reader-tabs-border);
            border-right: 0;
            background: var(--reader-tabs-surface);
            z-index: 8;
            overflow: hidden;
        }

        .reading-tab-panel.expanded {
            --reader-tabs-surface: var(--background);

            inset: 0 0 var(--app-primary-nav-height, calc(4.75rem + var(--safe-area-inset-bottom)))
                0;
            padding: calc(var(--safe-area-inset-top) + 1rem)
                max(1rem, var(--safe-area-inset-left, 0rem))
                calc(var(--safe-area-inset-bottom) + 1rem)
                max(1rem, var(--safe-area-inset-right, 0rem));
            border-top: 0;
            background: var(--reader-tabs-surface);
            overflow-y: auto;
            animation: reading-tabs-mobile-in 0.22s ease-out;
        }

        :global(.darkmode) .reading-tab-panel {
            --reader-tabs-surface: var(--color-grey-900);
            --reader-tabs-border: var(--color-grey-800);
            --reader-tabs-text: var(--color-grey-300);
            --reader-tabs-muted: var(--color-grey-400);
            --reader-tabs-strong: var(--color-grey-50);
            --reader-tabs-hover-bg: var(--color-grey-800);
            --reader-tabs-tab-bg: var(--color-grey-800);
            --reader-tabs-tab-border: var(--color-grey-700);
            --reader-tabs-tab-hover-border: var(--color-grey-600);
            --reader-tabs-icon-bg: var(--color-grey-800);
            --reader-tabs-icon-text: var(--color-grey-200);
            --reader-tabs-active-bg: var(--background);
            --reader-tabs-active-border: var(--color-grey-700);
            --reader-tabs-active-text: var(--color-grey-50);
            --reader-tabs-active-muted: var(--color-grey-300);
            --reader-tabs-active-icon-bg: var(--color-grey-700);
            --reader-tabs-active-icon-text: var(--color-grey-50);
            --reader-tabs-utility-active-bg: var(--color-grey-800);
            --reader-tabs-utility-active-border: var(--color-grey-700);
            --reader-tabs-utility-active-text: var(--color-grey-100);
        }

        :global(.darkmode) .reading-tab-panel.expanded {
            --reader-tabs-surface: var(--background);
        }

        .reading-tab-panel.mobileDock {
            position: fixed;
            right: 0;
            bottom: calc(
                var(--app-primary-nav-height, calc(4.75rem + var(--safe-area-inset-bottom))) +
                    var(--reader-tabs-mobile-bottom-offset, 0px)
            );
            left: 0;
            display: block;
            width: 100%;
            height: 0;
            max-height: 0;
            min-height: 0;
            padding: 0;
            border: 0;
            background: transparent;
            overflow: visible;
            pointer-events: none;
            z-index: 8;
        }

        .reading-tab-panel::before {
            content: none;
        }

        .reading-tab-panel.collapsed .collapse-button::after,
        .reading-tab-panel.collapsed .rail-action::after {
            content: none;
        }
    }

    @keyframes reading-tabs-expanded-in {
        from {
            opacity: 0;
            transform: scale(0.985);
        }

        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    @keyframes reading-tabs-mobile-in {
        from {
            transform: translateY(100%);
        }

        to {
            transform: translateY(0);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .reading-tab-panel.expanded {
            animation: none;
        }
    }
</style>
